/**
 * Public Reservation Page - Book a court at a club
 * Accessible via QR code or direct link: /reservar/{club-id}
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, MapPin, Phone, CreditCard, Banknote,
  ChevronLeft, ChevronRight, Check, AlertCircle, Play,
  Loader2, Info, X, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import { toast } from 'sonner';

// Clipper info component
const ClipperInfo = () => (
  <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
        <Play className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h4 className="font-medium text-primary">🎬 Cancha con Sistema de Replay</h4>
        <p className="text-sm text-muted-foreground mt-1">
          Esta cancha cuenta con el sistema <strong>Clipper</strong>. 
          Podrás ver repeticiones de tus jugadas desde tu celular durante y después del partido.
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">📱 Ver jugadas al instante</span>
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">💾 Guarda tus mejores momentos</span>
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">📤 Comparte con amigos</span>
        </div>
      </div>
    </div>
  </div>
);

// Court card
const CourtCard = ({ court, selected, onSelect }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={() => onSelect(court)}
    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
      selected 
        ? 'border-primary bg-primary/10' 
        : 'border-border hover:border-primary/50'
    }`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{court.name}</h3>
          {court.has_hiksports && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary flex items-center gap-1">
              <Play className="w-3 h-3" /> Replay
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1 capitalize">{court.court_type}</p>
        {court.features?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {court.features.slice(0, 3).map((f, i) => (
              <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded">{f}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-right">
        <p className="text-xl font-bold text-primary">${court.price_per_hour}</p>
        <p className="text-xs text-muted-foreground">/hora</p>
      </div>
    </div>
  </motion.button>
);

// Time slot button
const TimeSlot = ({ slot, selected, onSelect }) => (
  <button
    onClick={() => slot.available && onSelect(slot)}
    disabled={!slot.available}
    className={`p-3 rounded-lg text-center transition-all ${
      !slot.available 
        ? 'bg-secondary/30 text-muted-foreground cursor-not-allowed line-through' 
        : selected 
          ? 'bg-primary text-primary-foreground' 
          : 'bg-secondary hover:bg-secondary/80'
    }`}
  >
    <p className="font-medium">{slot.start_time}</p>
    <p className="text-xs">${slot.price}</p>
  </button>
);

// Confirmation modal
const ConfirmationModal = ({ reservation, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl p-6 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
        <Check className="w-8 h-8 text-green-500" />
      </div>
      
      <h2 className="font-display font-bold text-xl mb-2">¡Reservación Confirmada!</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Enviamos los detalles a tu correo electrónico
      </p>
      
      <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-4 my-4">
        <p className="text-sm text-muted-foreground mb-1">Tu código de confirmación</p>
        <p className="text-4xl font-display font-bold tracking-[0.3em] text-primary">
          {reservation.confirmation_code}
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          📱 Muestra este código al llegar al club
        </p>
      </div>
      
      <div className="text-left space-y-2 mb-4 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Cancha</span>
          <span className="font-medium">{reservation.court_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Fecha</span>
          <span className="font-medium">
            {new Date(reservation.date + 'T12:00:00').toLocaleDateString('es-MX', { 
              weekday: 'long', day: 'numeric', month: 'long' 
            })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Horario</span>
          <span className="font-medium">{reservation.start_time} - {reservation.end_time}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total</span>
          <span className="font-bold text-primary">${reservation.price}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Pago</span>
          <span className="font-medium">
            {reservation.payment_method === 'at_club' ? 'Al llegar al club' : 'En línea'}
          </span>
        </div>
      </div>

      {reservation.has_hiksports && (
        <div className="bg-primary/10 rounded-lg p-3 mb-4 text-sm text-left">
          <p className="font-medium text-primary">🎬 Esta cancha tiene Clipper</p>
          <p className="text-muted-foreground text-xs">
            Podrás ver las repeticiones de tus jugadas desde tu celular
          </p>
        </div>
      )}
      
      <Button onClick={onClose} className="w-full">
        Entendido
      </Button>
    </motion.div>
  </motion.div>
);

// Login prompt modal
const LoginPrompt = ({ onClose, clubId }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl p-6"
      >
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-display font-bold text-xl">Inicia sesión para reservar</h2>
          <p className="text-muted-foreground text-sm mt-2">
            Necesitas una cuenta en Clipper para hacer tu reservación
          </p>
        </div>
        
        <div className="space-y-3">
          <Button 
            className="w-full" 
            onClick={() => navigate(`/auth?redirect=/reservar/${clubId}`)}
          >
            Iniciar sesión
          </Button>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(`/auth?mode=register&redirect=/reservar/${clubId}`)}
          >
            Crear cuenta
          </Button>
          <Button variant="ghost" className="w-full" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function PublicReservation() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [courts, setCourts] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Booking state
  const [step, setStep] = useState(1); // 1: court, 2: date/time, 3: payment
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('at_club');
  const [notes, setNotes] = useState('');
  
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    fetchClubInfo();
  }, [clubId]); // fetchClubInfo is defined below

  useEffect(() => {
    if (selectedCourt && selectedDate) {
      fetchAvailability();
    }
  }, [selectedCourt, selectedDate]); // fetchAvailability is defined below

  const fetchClubInfo = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/reservations/public/club/${clubId}`);
      setClub(res.data.club);
      setCourts(res.data.courts);
      setSettings(res.data.booking_settings);
    } catch (err) {
      console.error('Error fetching club:', err);
      toast.error('Club no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      setLoadingSlots(true);
      setSelectedSlot(null);
      const res = await api.get(
        `/reservations/public/club/${clubId}/availability?court_id=${selectedCourt.id}&date=${selectedDate}`
      );
      setSlots(res.data.slots);
    } catch (err) {
      console.error('Error fetching slots:', err);
      toast.error('Error cargando horarios');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      setBooking(true);
      const res = await api.post(`/reservations/public/club/${clubId}/book`, {
        court_id: selectedCourt.id,
        date: selectedDate,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        payment_method: paymentMethod,
        notes: notes || undefined,
      });
      
      // If online payment, redirect to Mercado Pago
      if (res.data.requires_payment && res.data.payment_url) {
        toast.success('Redirigiendo a Mercado Pago...');
        window.location.href = res.data.payment_url;
        return;
      }
      
      // If pay at club, show confirmation
      setConfirmation(res.data.reservation);
      toast.success('¡Reservación confirmada! Revisa tu email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reservar');
    } finally {
      setBooking(false);
    }
  };

  // Generate date options (next N days)
  const dateOptions = [];
  const maxDays = settings.max_advance_days || 14;
  for (let i = 0; i < maxDays; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    dateOptions.push({
      value: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }),
      isToday: i === 0,
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-display font-bold text-xl mb-2">Club no encontrado</h1>
          <p className="text-muted-foreground mb-4">El club que buscas no existe o no tiene reservaciones habilitadas</p>
          <Button onClick={() => navigate('/')}>Ir al inicio</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            {club.logo_url ? (
              <img src={club.logo_url} alt={club.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-display font-bold">{club.name}</h1>
              <p className="text-xs text-muted-foreground">Reserva tu cancha</p>
            </div>
          </div>
          
          {isAuthenticated ? (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <User className="w-3 h-3" />
              {user?.first_name}
            </span>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate(`/auth?redirect=/reservar/${clubId}`)}>
              Iniciar sesión
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 pb-32">
        {/* Club Info */}
        {(club.address || club.phone) && (
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-6">
            {club.address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {club.address}
              </span>
            )}
            {club.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {club.phone}
              </span>
            )}
          </div>
        )}

        {/* Step 1: Select Court */}
        <div className="mb-6">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">1</span>
            Selecciona cancha
          </h2>
          <div className="grid gap-3">
            {courts.map((court) => (
              <CourtCard
                key={court.id}
                court={court}
                selected={selectedCourt?.id === court.id}
                onSelect={(c) => { setSelectedCourt(c); setStep(2); }}
              />
            ))}
          </div>
        </div>

        {/* Step 2: Select Date & Time */}
        {selectedCourt && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">2</span>
              Elige fecha y hora
            </h2>

            {selectedCourt.has_hiksports && <ClipperInfo />}

            {/* Date selector */}
            <div className="mb-4">
              <Label className="mb-2 block">Fecha</Label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {dateOptions.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setSelectedDate(d.value)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                      selectedDate === d.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <p className="font-medium">{d.label}</p>
                    {d.isToday && <p className="text-xs">Hoy</p>}
                  </button>
                ))}
              </div>
            </div>

            {/* Time slots */}
            {selectedDate && (
              <div>
                <Label className="mb-2 block">Horario</Label>
                {loadingSlots ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {slots.map((slot) => (
                      <TimeSlot
                        key={slot.start_time}
                        slot={slot}
                        selected={selectedSlot?.start_time === slot.start_time}
                        onSelect={(s) => { setSelectedSlot(s); setStep(3); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Step 3: Payment Method */}
        {selectedSlot && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">3</span>
              Método de pago
            </h2>

            <div className="grid gap-3">
              <button
                onClick={() => setPaymentMethod('at_club')}
                className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  paymentMethod === 'at_club' ? 'border-primary bg-primary/10' : 'border-border'
                }`}
              >
                <Banknote className={`w-6 h-6 ${paymentMethod === 'at_club' ? 'text-primary' : ''}`} />
                <div>
                  <p className="font-medium">Pagar al llegar</p>
                  <p className="text-sm text-muted-foreground">Paga en efectivo o tarjeta en el club</p>
                </div>
              </button>
              
              <button
                onClick={() => setPaymentMethod('online')}
                className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  paymentMethod === 'online' ? 'border-primary bg-primary/10' : 'border-border'
                }`}
              >
                <CreditCard className={`w-6 h-6 ${paymentMethod === 'online' ? 'text-primary' : ''}`} />
                <div>
                  <p className="font-medium">Pagar ahora</p>
                  <p className="text-sm text-muted-foreground">Pago seguro con Mercado Pago</p>
                </div>
              </button>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <Label>Notas (opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: Somos 4 personas, necesitamos pelotas"
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom Bar */}
      {selectedSlot && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                {selectedCourt?.name} • {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}
              </p>
              <p className="font-medium">
                {selectedSlot.start_time} - {selectedSlot.end_time} • 
                <span className="text-primary font-bold ml-1">${selectedSlot.price}</span>
              </p>
            </div>
            <Button onClick={handleBooking} disabled={booking}>
              {booking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reservando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showLoginPrompt && (
          <LoginPrompt onClose={() => setShowLoginPrompt(false)} clubId={clubId} />
        )}
        {confirmation && (
          <ConfirmationModal 
            reservation={confirmation} 
            onClose={() => {
              setConfirmation(null);
              // Reset form
              setSelectedCourt(null);
              setSelectedDate(null);
              setSelectedSlot(null);
              setStep(1);
            }} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}
